import React from 'react';
import axios from 'axios';
import { post, get } from '../../util/api';
import { Box, Button, Chip, CircularProgress, Collapse, IconButton, MenuItem, Select, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';

const API_URL = process.env.REACT_APP_API_URL || 'http://51.15.11.170:4200/api';

const PRESET_QUERIES = [
	{
		label: 'Phone Incoming',
		query: `SELECT pid, phone, data.path, data.method, data.statusCode, data.body, data.response, data.error
FROM stream
WHERE type = 'incoming' AND phone = 0
LIMIT 100`,
	},
	{
		label: 'Phone Errors',
		query: `SELECT pid, phone, data.path, data.error
FROM stream
WHERE type = 'error' AND phone = 0
LIMIT 100`,
	},
	{
		label: 'Instance Sizes',
		query: `SELECT last(data.phone_id) as phone_id, last(data.pid) as pid, last(data.message) as message, last(@Timestamp) as ts FROM stream WHERE data.type = 'boot_instance_size' GROUP BY data.phone_id LIMIT 5000`,
	},
	{
		label: 'Last Messages',
		query: `SELECT last(phone) as phone, last(@Timestamp) as ts FROM stream WHERE type = 'outgoing' AND data.body.type <> 'message' AND data.body.type <> 'status' AND data.body.type <> 'live_event' GROUP BY phone LIMIT 5000`,
	},
];

function toTRLocal(date) {
	// UTC+3
	const tr = new Date(date.getTime() + 3 * 60 * 60 * 1000);
	return tr.toISOString().slice(0, 16);
}

function getDefaultRange() {
	const now = new Date();
	const start = new Date(now.getTime() - 2 * 60 * 60 * 1000);
	return { start: toTRLocal(start), end: toTRLocal(now) };
}

function parseColumns(query) {
	const match = query.match(/SELECT\s+([\s\S]+?)\s+FROM/i);
	if (!match) return [];
	return match[1].split(',').map((col) => {
		col = col.trim();
		const asMatch = col.match(/\bAS\s+(\w+)\s*$/i);
		if (asMatch) return asMatch[1];
		const wordMatch = col.match(/(\w+)\s*$/);
		return wordMatch ? wordMatch[1] : col;
	});
}

// ── Phone Actions ──────────────────────────────────────────────────────────

const PHONE_ACTIONS = [
	{ label: 'Info',     action: 'info',            method: 'GET' },
	{ label: 'Status',   action: 'status',           method: 'GET' },
	{ label: 'Instance', action: 'instance_status',  method: 'GET' },
	{ label: 'Queue',    action: 'queue',             method: 'GET' },
	{ label: 'Screen',   action: 'screen',            method: 'GET', isImage: true },
	{ label: 'Redeploy', action: 'redeploy',          method: 'POST', danger: true },
	{ label: 'Logout',   action: 'logout',            method: 'POST', danger: true },
	{ label: 'Clear',    action: 'clear',             method: 'POST', danger: true },
	{ label: 'Factory Reset', action: 'factoryReset', method: 'POST', danger: true },
];

function PhoneActionsPanel({ phoneId, colSpan, rowContent }) {
	const [open, setOpen] = React.useState(false);
	const [results, setResults] = React.useState({});
	const [loadingMap, setLoadingMap] = React.useState({});
	const [pendingConfirm, setPendingConfirm] = React.useState(null);

	async function runAction(act) {
		if (act.danger && pendingConfirm !== act.action) {
			setPendingConfirm(act.action);
			return;
		}
		setPendingConfirm(null);
		setLoadingMap((l) => ({ ...l, [act.action]: true }));
		try {
			let result;
			if (act.isImage) {
				const resp = await axios.get(`${API_URL}/maytapi/admin/phone/${phoneId}/${act.action}`, {
					responseType: 'blob',
					withCredentials: true,
				});
				result = { _image: URL.createObjectURL(resp.data) };
			} else if (act.method === 'POST') {
				const resp = await post(`/maytapi/admin/phone/${phoneId}/${act.action}`, {});
				result = resp.data;
			} else {
				result = await get(`/maytapi/admin/phone/${phoneId}/${act.action}`);
			}
			setResults((r) => ({ ...r, [act.action]: result }));
		} catch (e) {
			setResults((r) => ({ ...r, [act.action]: { error: e.message } }));
		}
		setLoadingMap((l) => ({ ...l, [act.action]: false }));
	}

	const readActions  = PHONE_ACTIONS.filter((a) => a.method === 'GET');
	const writeActions = PHONE_ACTIONS.filter((a) => a.method === 'POST');

	return (
		<React.Fragment>
			<TableRow hover>
				{rowContent}
				<TableCell sx={{ p: 0.5, width: 36 }}>
					<IconButton size="small" onClick={() => { setOpen((o) => !o); setPendingConfirm(null); }}>
						{open ? <ExpandMoreIcon fontSize="small" /> : <MoreVertIcon fontSize="small" />}
					</IconButton>
				</TableCell>
			</TableRow>
			<TableRow sx={{ '& > td': { py: 0 } }}>
				<TableCell colSpan={colSpan + 1} sx={{ p: 0, border: open ? undefined : 0 }}>
					<Collapse in={open} unmountOnExit>
						<Box sx={{ p: 1.5, backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: results && Object.keys(results).length > 0 ? 1.5 : 0 }}>
								{readActions.map((act) => (
									<Button
										key={act.action}
										size="small"
										variant="outlined"
										color="info"
										disabled={!!loadingMap[act.action]}
										startIcon={loadingMap[act.action] ? <CircularProgress size={12} /> : null}
										onClick={() => runAction(act)}
										sx={{ textTransform: 'none', fontSize: 11 }}
									>
										{act.label}
									</Button>
								))}
								<Box sx={{ width: '100%', height: 0 }} />
								{writeActions.map((act) => (
									pendingConfirm === act.action ? (
										<Box key={act.action} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
											<Typography variant="caption" color="error">Emin misin?</Typography>
											<Button size="small" variant="contained" color="error" sx={{ fontSize: 11, textTransform: 'none' }} onClick={() => runAction(act)}>Evet, {act.label}</Button>
											<Button size="small" variant="outlined" sx={{ fontSize: 11, textTransform: 'none' }} onClick={() => setPendingConfirm(null)}>İptal</Button>
										</Box>
									) : (
										<Button
											key={act.action}
											size="small"
											variant="outlined"
											color="error"
											disabled={!!loadingMap[act.action]}
											startIcon={loadingMap[act.action] ? <CircularProgress size={12} /> : null}
											onClick={() => runAction(act)}
											sx={{ textTransform: 'none', fontSize: 11 }}
										>
											{act.label}
										</Button>
									)
								))}
							</Box>
							{Object.entries(results).map(([action, result]) => (
								<Box key={action} sx={{ mb: 1 }}>
									<Chip label={action} size="small" sx={{ mb: 0.5, fontSize: 11 }} />
									{result._image ? (
										<Box><img src={result._image} alt="screen" style={{ maxWidth: '100%', maxHeight: 300, display: 'block', borderRadius: 4 }} /></Box>
									) : (
										<Box sx={{ backgroundColor: '#282c34', borderRadius: 1, p: 1, maxHeight: 200, overflow: 'auto' }}>
											<JsonNode value={result} depth={0} defaultOpen />
										</Box>
									)}
								</Box>
							))}
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</React.Fragment>
	);
}

// ── Helpers ────────────────────────────────────────────────────────────────

function queueColor(n) {
	if (n == null) return null;
	if (n === 0)    return { bg: '#e8f5e9', color: '#2e7d32' };
	if (n <= 100)   return { bg: '#fffde7', color: '#f9a825' };
	if (n <= 1000)  return { bg: '#fff3e0', color: '#e65100' };
	return              { bg: '#ffebee', color: '#c62828' };
}

function memColor(totalMB) {
	const gb = totalMB / 1024;
	if (gb <= 3)  return { bg: '#e8f5e9', text: '#2e7d32', label: 'Normal' };
	if (gb <= 7)  return { bg: '#fff9c4', text: '#f57f17', label: 'High' };
	if (gb <= 10) return { bg: '#ffe0b2', text: '#e65100', label: 'Very High' };
	return              { bg: '#ffebee', text: '#c62828', label: 'Critical' };
}

function timeAgo(date) {
	if (!date) return '—';
	const s = Math.floor((Date.now() - date.getTime()) / 1000);
	if (s < 60)   return `${s}s ago`;
	const m = Math.floor(s / 60);
	if (m < 60)   return `${m}m ago`;
	const h = Math.floor(m / 60);
	if (h < 24)   return `${h}h ago`;
	return `${Math.floor(h / 24)}d ago`;
}

// ── Instance Size Table ────────────────────────────────────────────────────

function parseMemoryMB(str) {
	if (!str || typeof str !== 'string') return null;
	const result = {};
	str.split(',').forEach((part) => {
		const m = part.trim().match(/^(\w+)=([\d.]+)\s*(GB|MB|KB|B)?/);
		if (m) {
			const val = parseFloat(m[2]);
			const unit = m[3] || 'MB';
			let mb = val;
			if (unit === 'GB') mb = val * 1024;
			else if (unit === 'KB') mb = val / 1024;
			else if (unit === 'B') mb = val / (1024 * 1024);
			result[m[1]] = Math.round(mb * 100) / 100;
		}
	});
	return Object.keys(result).length > 0 ? result : null;
}

function fmtMB(mb) {
	if (mb >= 1024) return (mb / 1024).toFixed(2) + ' GB';
	return mb.toFixed(0) + ' MB';
}

function parseMBInput(val) {
	if (!val) return null;
	const m = String(val).trim().match(/^([\d.]+)\s*(GB|MB|KB)?$/i);
	if (!m) return null;
	const n = parseFloat(m[1]);
	const u = (m[2] || 'GB').toUpperCase();
	if (u === 'GB') return n * 1024;
	if (u === 'KB') return n / 1024;
	return n;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function InstanceSizeTable({ rows, onDisplayedChange, queueMap = {}, queueLoading = false, lastMsgMap = {}, lastMsgLoading = false, versionMap = {}, versionLoading = false }) {
	const [sortKey, setSortKey] = React.useState('total');
	const [sortDir, setSortDir] = React.useState('desc');
	const [phoneFilter, setPhoneFilter] = React.useState('');
	const [pidFilter, setPidFilter] = React.useState('');
	const [memFilters, setMemFilters] = React.useState({});
	const [versionFilter, setVersionFilter] = React.useState({ apiVersion: '', lastVersion: '' });
	const [page, setPage] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(100);

	const parsed = React.useMemo(() => {
		return rows
			.map((row) => {
				const arr = Array.isArray(row) ? row : Object.values(row);
				const phoneId = arr.find((v) => typeof v === 'number' && v > 100 && v < 1e15);
				const tsTicks = arr.find((v) => typeof v === 'number' && v > 6e17);
				const pid     = arr.find((v) => typeof v === 'string' && UUID_RE.test(v));
				const msgStr  = arr.find((v) => typeof v === 'string' && v.includes('total='));
				const mem = parseMemoryMB(msgStr);
				if (!mem) return null;
				const ts = tsTicks ? new Date((tsTicks - 621355968000000000) / 10000) : null;
				return { phoneId, pid: pid || '', ts, raw: msgStr, ...mem };
			})
			.filter(Boolean);
	}, [rows]);

	const memKeys = React.useMemo(() => {
		const keys = new Set();
		parsed.forEach((r) => Object.keys(r).forEach((k) => { if (!['phoneId', 'pid', 'ts', 'raw'].includes(k)) keys.add(k); }));
		return [...keys];
	}, [parsed]);

	function toggleSort(key) {
		if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
		else { setSortKey(key); setSortDir('desc'); }
	}

	function setMemFilter(key, field, value) {
		setPage(0);
		setMemFilters((prev) => ({ ...prev, [key]: { op: '>', val: '', ...prev[key], [field]: value } }));
	}

	function clearMemFilter(key) {
		setPage(0);
		setMemFilters((prev) => { const n = { ...prev }; delete n[key]; return n; });
	}

	const displayed = React.useMemo(() => {
		let list = parsed.filter((r) => !phoneFilter || String(r.phoneId).includes(phoneFilter));
		if (pidFilter) list = list.filter((r) => r.pid.includes(pidFilter));
		Object.entries(memFilters).forEach(([key, { op, val }]) => {
			const threshold = parseMBInput(val);
			if (threshold === null) return;
			list = list.filter((r) => {
				const v = r[key] ?? 0;
				if (op === '>') return v > threshold;
				if (op === '<') return v < threshold;
				return Math.abs(v - threshold) < 1;
			});
		});
		if (versionFilter.apiVersion) {
			list = list.filter((r) => (versionMap[r.phoneId]?.api_version || '').includes(versionFilter.apiVersion));
		}
		if (versionFilter.lastVersion !== '') {
			const expected = versionFilter.lastVersion === 'true';
			list = list.filter((r) => Boolean(versionMap[r.phoneId]?.lastVersion) === expected);
		}
		list = [...list].sort((a, b) => {
			const av = sortKey === 'queue' ? (queueMap[a.phoneId] ?? -1) : (a[sortKey] ?? 0);
			const bv = sortKey === 'queue' ? (queueMap[b.phoneId] ?? -1) : (b[sortKey] ?? 0);
			return sortDir === 'desc' ? bv - av : av - bv;
		});
		return list;
	}, [parsed, sortKey, sortDir, phoneFilter, pidFilter, memFilters, queueMap, versionFilter, versionMap]);

	React.useEffect(() => {
		if (onDisplayedChange) onDisplayedChange(displayed);
	}, [displayed, onDisplayedChange]);

	const totalPages = Math.ceil(displayed.length / pageSize);
	const paged = displayed.slice(page * pageSize, (page + 1) * pageSize);

	function SortIcon({ k }) {
		if (sortKey !== k) return null;
		return sortDir === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 12, ml: 0.5 }} /> : <ArrowUpwardIcon sx={{ fontSize: 12, ml: 0.5 }} />;
	}

	return (
		<Box>
			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
				<TextField
					size="small"
					placeholder="phone_id"
					label="phone_id"
					value={phoneFilter}
					onChange={(e) => setPhoneFilter(e.target.value)}
					sx={{ width: 130 }}
					InputLabelProps={{ shrink: true }}
				/>
				<TextField
					size="small"
					placeholder="pid (uuid)"
					label="pid"
					value={pidFilter}
					onChange={(e) => setPidFilter(e.target.value)}
					sx={{ width: 220 }}
					InputLabelProps={{ shrink: true }}
				/>
				{memKeys.map((k) => {
					const f = memFilters[k] || { op: '>', val: '' };
					const active = f.val !== '';
					return (
						<Box key={k} sx={{ display: 'flex', alignItems: 'center', border: active ? '1px solid' : '1px solid transparent', borderColor: active ? 'primary.main' : 'divider', borderRadius: 1, px: 0.5 }}>
							<Typography variant="caption" sx={{ mr: 0.5, fontWeight: 'bold', color: active ? 'primary.main' : 'text.secondary' }}>{k}</Typography>
							<Select
								size="small"
								value={f.op}
								onChange={(e) => setMemFilter(k, 'op', e.target.value)}
								sx={{ fontSize: 12, height: 28, '.MuiOutlinedInput-notchedOutline': { border: 'none' }, minWidth: 42 }}
							>
								<MenuItem value=">">{'>'}</MenuItem>
								<MenuItem value="<">{'<'}</MenuItem>
								<MenuItem value="=">{'='}</MenuItem>
							</Select>
							<TextField
								size="small"
								placeholder="1.5 GB"
								value={f.val}
								onChange={(e) => setMemFilter(k, 'val', e.target.value)}
								sx={{ width: 80, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, px: 0.5 }, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
							/>
							{active && (
								<Button size="small" onClick={() => clearMemFilter(k)} sx={{ minWidth: 0, p: 0.2, fontSize: 11, color: 'text.secondary' }}>✕</Button>
							)}
						</Box>
					);
				})}
				{(() => {
					const active = versionFilter.apiVersion !== '' || versionFilter.lastVersion !== '';
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid', borderColor: active ? 'primary.main' : 'divider', borderRadius: 1, px: 0.5 }}>
							<Typography variant="caption" sx={{ mr: 0.5, fontWeight: 'bold', color: active ? 'primary.main' : 'text.secondary' }}>version</Typography>
							<TextField
								size="small"
								placeholder="1.5.1"
								value={versionFilter.apiVersion}
								onChange={(e) => { setVersionFilter((f) => ({ ...f, apiVersion: e.target.value })); setPage(0); }}
								sx={{ width: 70, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, px: 0.5 }, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
							/>
							<Select
								size="small"
								value={versionFilter.lastVersion}
								onChange={(e) => { setVersionFilter((f) => ({ ...f, lastVersion: e.target.value })); setPage(0); }}
								displayEmpty
								sx={{ fontSize: 12, height: 28, '.MuiOutlinedInput-notchedOutline': { border: 'none' }, minWidth: 50 }}
							>
								<MenuItem value="">All</MenuItem>
								<MenuItem value="true">✓</MenuItem>
								<MenuItem value="false">✗</MenuItem>
							</Select>
							{active && (
								<Button size="small" onClick={() => { setVersionFilter({ apiVersion: '', lastVersion: '' }); setPage(0); }} sx={{ minWidth: 0, p: 0.2, fontSize: 11, color: 'text.secondary' }}>✕</Button>
							)}
						</Box>
					);
				})()}
				<Box sx={{ flex: 1 }} />
				<Typography variant="caption" color="text.secondary">{displayed.length} phones</Typography>
				<Select size="small" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} sx={{ fontSize: 12, height: 28 }}>
					{[50, 100, 250, 500].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
				</Select>
				<Button size="small" disabled={page === 0} onClick={() => setPage(0)} sx={{ minWidth: 0, px: 1, fontSize: 11 }}>«</Button>
				<Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} sx={{ minWidth: 0, px: 1, fontSize: 11 }}>‹</Button>
				<Typography variant="caption">{page + 1} / {totalPages || 1}</Typography>
				<Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} sx={{ minWidth: 0, px: 1, fontSize: 11 }}>›</Button>
				<Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)} sx={{ minWidth: 0, px: 1, fontSize: 11 }}>»</Button>
			</Box>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold', color: 'text.disabled', width: 40 }}>#</TableCell>
						<TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort('phoneId')}>
							phone_id <SortIcon k="phoneId" />
						</TableCell>
						<TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: 'text.secondary' }}>pid</TableCell>
						<TableCell sx={{ fontWeight: 'bold' }}>Memory</TableCell>
						{memKeys.map((k) => (
							<TableCell key={k} sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort(k)}>
								{k} <SortIcon k={k} />
							</TableCell>
						))}
						{/* Queue kolonu şimdilik kapalı */}
						<TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Version</TableCell>
						<TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Last Update</TableCell>
						<TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Last Msg</TableCell>
						<TableCell sx={{ width: 36 }} />
					</TableRow>
				</TableHead>
				<TableBody>
					{paged.map((r, i) => {
						const q  = queueMap[r.phoneId];
						const qc = queueColor(q);
						const mc = r.total != null ? memColor(r.total) : null;
						return (
							<PhoneActionsPanel key={i} phoneId={r.phoneId} colSpan={7 + memKeys.length} rowContent={
								<>
									<TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.disabled' }}>{page * pageSize + i + 1}</TableCell>
									<TableCell sx={{ fontFamily: 'monospace', fontSize: 12, backgroundColor: mc ? mc.bg : 'inherit' }}>{r.phoneId}</TableCell>
									<TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', backgroundColor: mc ? mc.bg : 'inherit' }} title={r.pid}>{r.pid}</TableCell>
									<TableCell sx={{ backgroundColor: mc ? mc.bg : 'inherit' }}>
										{mc && <Chip label={mc.label} size="small" sx={{ fontSize: 11, height: 20, fontWeight: 'bold', backgroundColor: mc.bg, color: mc.text, border: `1px solid ${mc.text}` }} />}
									</TableCell>
									{memKeys.map((k) => (
										<TableCell key={k} sx={{ fontFamily: 'monospace', fontSize: 12, backgroundColor: mc ? mc.bg : 'inherit' }}>
											{r[k] != null ? fmtMB(r[k]) : '—'}
										</TableCell>
									))}
									{/* Queue cell şimdilik kapalı */}
									<TableCell sx={{ fontFamily: 'monospace', fontSize: 12, backgroundColor: mc ? mc.bg : 'inherit', whiteSpace: 'nowrap' }}>
										{versionLoading ? <CircularProgress size={10} /> : (() => {
											const v = versionMap[r.phoneId];
											if (!v) return '—';
											return <>{v.api_version || '—'}<span style={{ marginLeft: 4, color: v.lastVersion ? '#2e7d32' : '#c62828' }}>({v.lastVersion ? '✓' : '✗'})</span></>;
										})()}
									</TableCell>
									<TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', backgroundColor: mc ? mc.bg : 'inherit' }} title={r.ts ? r.ts.toISOString() : ''}>
										{timeAgo(r.ts)}
									</TableCell>
									<TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', backgroundColor: mc ? mc.bg : 'inherit' }}>
										{lastMsgLoading && !lastMsgMap[r.phoneId]
											? <CircularProgress size={10} />
											: timeAgo(lastMsgMap[r.phoneId])
										}
									</TableCell>
								</>
							} />
						);
					})}
				</TableBody>
			</Table>
		</Box>
	);
}

// ── JSON Tree ──────────────────────────────────────────────────────────────

function JsonValue({ value }) {
	if (value === null) return <span style={{ color: '#6b737e' }}>null</span>;
	if (value === undefined) return <span style={{ color: '#6b737e' }}>undefined</span>;
	if (typeof value === 'boolean') return <span style={{ color: '#56b6c2' }}>{String(value)}</span>;
	if (typeof value === 'number') return <span style={{ color: '#d19a66' }}>{value}</span>;
	if (typeof value === 'string') return <span style={{ color: '#98c379' }}>"{value}"</span>;
	return null;
}

function JsonNode({ label, value, depth = 0, defaultOpen = false }) {
	const [open, setOpen] = React.useState(defaultOpen);
	const indent = depth * 16;
	const isObj = value !== null && typeof value === 'object';

	if (!isObj) {
		return (
			<div style={{ paddingLeft: indent, lineHeight: '1.6', display: 'flex', alignItems: 'baseline', gap: 4 }}>
				{label !== undefined && <span style={{ color: '#c678dd' }}>{label}: </span>}
				<JsonValue value={value} />
			</div>
		);
	}

	const isArr = Array.isArray(value);
	const entries = isArr ? value.map((v, i) => [i, v]) : Object.entries(value);
	const preview = entries
		.slice(0, 3)
		.map(([k, v]) => (isArr ? (typeof v === 'object' && v ? '{…}' : String(v)) : `${k}: ${typeof v === 'object' && v ? '{…}' : JSON.stringify(v)}`))
		.join(', ');
	const bracket = isArr ? ['[', ']'] : ['{', '}'];

	return (
		<div style={{ paddingLeft: indent }}>
			<div
				onClick={() => setOpen((o) => !o)}
				style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', lineHeight: '1.6', userSelect: 'none' }}
			>
				<span style={{ color: '#abb2bf', display: 'flex', alignItems: 'center', width: 14, flexShrink: 0 }}>
					{open ? <ExpandMoreIcon style={{ fontSize: 14 }} /> : <ChevronRightIcon style={{ fontSize: 14 }} />}
				</span>
				{label !== undefined && <span style={{ color: '#c678dd', marginRight: 4 }}>{label}:</span>}
				{open ? (
					<span style={{ color: '#abb2bf' }}>{bracket[0]}</span>
				) : (
					<span style={{ color: '#abb2bf' }}>
						{bracket[0]} <span style={{ color: '#6b737e', fontStyle: 'italic' }}>{preview}{entries.length > 3 ? ', …' : ''}</span> {bracket[1]}
						<span style={{ color: '#6b737e', marginLeft: 6, fontSize: 11 }}>{entries.length} {isArr ? 'items' : 'keys'}</span>
					</span>
				)}
			</div>
			{open && (
				<>
					{entries.map(([k, v]) => (
						<JsonNode key={k} label={isArr ? undefined : k} value={v} depth={depth + 1} />
					))}
					<div style={{ paddingLeft: 14, color: '#abb2bf', lineHeight: '1.6' }}>{bracket[1]}</div>
				</>
			)}
		</div>
	);
}

// ── Row ────────────────────────────────────────────────────────────────────

function LogRow({ row, columns, index }) {
	const [expanded, setExpanded] = React.useState(false);

	function getSummary() {
		const parts = [];
		if (Array.isArray(row)) {
			row.forEach((val, i) => {
				const col = columns[i] || `col${i}`;
				if (val === null || val === undefined) return;
				if (typeof val === 'object') {
					const type = val.type || val['@l'] || '';
					const msg = val['@m'] || val.message || '';
					if (type) parts.push(<span key={col + 'type'} style={{ color: '#56b6c2', marginRight: 8 }}>{type}</span>);
					if (msg) parts.push(<span key={col + 'msg'} style={{ color: '#abb2bf' }}>{msg}</span>);
				} else {
					parts.push(<span key={col} style={{ color: i === 0 ? '#6b737e' : '#e06c75', marginRight: 8 }}>{String(val)}</span>);
				}
			});
		}
		return parts.length > 0 ? parts : <span style={{ color: '#6b737e' }}>row {index + 1}</span>;
	}

	return (
		<div
			style={{
				borderBottom: '1px solid #2c313a',
				fontFamily: 'monospace',
				fontSize: 12,
			}}
		>
			<div
				onClick={() => setExpanded((e) => !e)}
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 6,
					padding: '4px 8px',
					cursor: 'pointer',
					backgroundColor: expanded ? '#21252b' : 'transparent',
					transition: 'background 0.1s',
				}}
				onMouseEnter={(e) => { if (!expanded) e.currentTarget.style.background = '#1e2227'; }}
				onMouseLeave={(e) => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
			>
				<span style={{ color: '#abb2bf', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
					{expanded ? <ExpandMoreIcon style={{ fontSize: 14 }} /> : <ChevronRightIcon style={{ fontSize: 14 }} />}
				</span>
				<span style={{ flexShrink: 0, color: '#6b737e', width: 28, textAlign: 'right', marginRight: 4 }}>{index + 1}</span>
				<span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'baseline', overflow: 'hidden' }}>
					{getSummary()}
				</span>
			</div>
			{expanded && (
				<div style={{ padding: '6px 12px 6px 36px', backgroundColor: '#21252b' }}>
					{Array.isArray(row)
						? row.map((val, i) => (
								<JsonNode key={i} label={columns[i] || `col${i}`} value={val} depth={0} defaultOpen={false} />
						  ))
						: <JsonNode value={row} depth={0} defaultOpen />}
				</div>
			)}
		</div>
	);
}

// ── Queues Section ─────────────────────────────────────────────────────────

const STATUS_COLORS = {
	active:   { bg: '#e8f5e9', color: '#2e7d32' },
	disabled: { bg: '#fafafa', color: '#9e9e9e' },
	qr:       { bg: '#fff3e0', color: '#e65100' },
	error:    { bg: '#ffebee', color: '#c62828' },
};

function statusColor(status) {
	return STATUS_COLORS[status] || { bg: '#f3e5f5', color: '#6a1b9a' };
}

function QueuesSection({ queues, queueLoading: loading, error, onRefresh }) {
	const [sortKey, setSortKey] = React.useState('queue');
	const [sortDir, setSortDir] = React.useState('desc');
	const [filter, setFilter]   = React.useState('');

	function toggleSort(key) {
		if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
		else { setSortKey(key); setSortDir('desc'); }
	}

	function SortIcon({ k }) {
		if (sortKey !== k) return null;
		return sortDir === 'desc'
			? <ArrowDownwardIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle' }} />
			: <ArrowUpwardIcon   sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle' }} />;
	}

	const displayed = React.useMemo(() => {
		if (!queues) return [];
		let list = queues;
		if (filter) {
			const f = filter.toLowerCase();
			list = list.filter(
				(q) =>
					String(q.id).includes(f) ||
					(q.pid || '').toLowerCase().includes(f) ||
					(q.number || '').includes(f) ||
					(q.status || '').includes(f) ||
					(q.sessionId || '').toLowerCase().includes(f),
			);
		}
		return [...list].sort((a, b) => {
			const av = a[sortKey] ?? 0;
			const bv = b[sortKey] ?? 0;
			if (typeof av === 'string') return sortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
			return sortDir === 'desc' ? bv - av : av - bv;
		});
	}, [queues, filter, sortKey, sortDir]);

	const totalQueue = React.useMemo(() => (queues || []).reduce((s, q) => s + (q.queue || 0), 0), [queues]);

	return (
		<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
				<Button
					variant="outlined"
					size="small"
					startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
					onClick={onRefresh}
					disabled={loading}
				>
					Yenile
				</Button>
			</Box>

			{error && <Typography color="error" variant="body2">{error}</Typography>}

			{queues !== null && (
				<Box>
					<Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
						<TextField
							size="small"
							placeholder="id, pid, number, status…"
							label="Filtrele"
							value={filter}
							onChange={(e) => setFilter(e.target.value)}
							sx={{ width: 260 }}
							InputLabelProps={{ shrink: true }}
						/>
						<Typography variant="caption" color="text.secondary">
							{displayed.length} / {queues.length} kayıt &nbsp;·&nbsp; Toplam queue: <strong>{totalQueue.toLocaleString()}</strong>
						</Typography>
					</Box>

					<Box sx={{ overflowX: 'auto' }}>
						<Table size="small" sx={{ minWidth: 680 }}>
							<TableHead>
								<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
									<TableCell sx={{ fontWeight: 'bold', width: 40, color: 'text.disabled' }}>#</TableCell>
									<TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort('id')}>
										ID <SortIcon k="id" />
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>PID</TableCell>
									<TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort('status')}>
										Status <SortIcon k="status" />
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Number</TableCell>
									<TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Session ID</TableCell>
									<TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }} onClick={() => toggleSort('queue')}>
										Queue <SortIcon k="queue" />
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{displayed.map((q, i) => {
									const sc = statusColor(q.status);
									return (
										<TableRow key={q.id} hover>
											<TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.disabled' }}>{i + 1}</TableCell>
											<TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{q.id}</TableCell>
											<TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={q.pid}>
												{q.pid}
											</TableCell>
											<TableCell>
												<Chip
													label={q.status}
													size="small"
													sx={{ fontSize: 11, height: 20, backgroundColor: sc.bg, color: sc.color, fontWeight: 600 }}
												/>
											</TableCell>
											<TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{q.number}</TableCell>
											<TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>{q.sessionId}</TableCell>
											<TableCell sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, textAlign: 'right', color: q.queue > 0 ? 'error.main' : 'text.primary' }}>
												{(q.queue ?? 0).toLocaleString()}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</Box>
				</Box>
			)}
		</Box>
	);
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function SeqPage() {
	const [tab, setTab] = React.useState(0);

	const [queues, setQueues]                 = React.useState(null);
	const [queueLoading, setQueueLoading]     = React.useState(true);
	const [queueError, setQueueError]         = React.useState(null);
	const [lastMsgMap, setLastMsgMap]         = React.useState({});
	const [lastMsgLoading, setLastMsgLoading] = React.useState(true);
	const [versionMap, setVersionMap]         = React.useState({});
	const [versionLoading, setVersionLoading] = React.useState(true);

	const queueMap = React.useMemo(() => {
		if (!queues) return {};
		const map = {};
		queues.forEach((q) => { map[q.id] = q.queue; });
		return map;
	}, [queues]);

	function fetchQueues() {
		setQueueLoading(true);
		setQueueError(null);
		get('/maytapi/admin/queues')
			.then((data) => {
				if (data.success && Array.isArray(data.queues)) {
					setQueues(data.queues);
				} else {
					setQueueError(data.error || data.message || 'Başarısız yanıt');
					setQueues([]);
				}
			})
			.catch((err) => { setQueueError(err.message || 'İstek başarısız'); setQueues([]); })
			.finally(() => setQueueLoading(false));
	}

	function fetchLastMessages() {
		setLastMsgLoading(true);
		get('/maytapi/admin/lastMessages')
			.then((data) => {
				if (data.success) {
					const map = {};
					(data.rows || []).forEach((row) => {
						const arr = Array.isArray(row) ? row : Object.values(row);
						const phoneId = arr.find((v) => typeof v === 'number' && v > 100 && v < 1e15);
						const tsTicks = arr.find((v) => typeof v === 'number' && v > 6e17);
						if (phoneId && tsTicks) map[phoneId] = new Date((tsTicks - 621355968000000000) / 10000);
					});
					setLastMsgMap(map);
				}
			})
			.catch(() => {})
			.finally(() => setLastMsgLoading(false));
	}

	function fetchPhoneList() {
		setVersionLoading(true);
		get('/maytapi/admin/phoneList')
			.then((data) => {
				if (data.phones) {
					const map = {};
					data.phones.forEach((p) => { if (p.api_version !== undefined) map[p.id] = { api_version: p.api_version, lastVersion: p.lastVersion }; });
					setVersionMap(map);
				}
			})
			.catch(() => {})
			.finally(() => setVersionLoading(false));
	}

	React.useEffect(() => { /* fetchQueues(); */ fetchLastMessages(); fetchPhoneList(); }, []); // eslint-disable-line

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === 1 && queues === null) fetchQueues(); }} sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
				<Tab label="Seq Query" />
				<Tab label="Queues" />
			</Tabs>
			{tab === 0 && <SeqQueryPanel queueMap={queueMap} queueLoading={queueLoading} lastMsgMap={lastMsgMap} lastMsgLoading={lastMsgLoading} versionMap={versionMap} versionLoading={versionLoading} />}
			{tab === 1 && <QueuesSection queues={queues} queueLoading={queueLoading} error={queueError} onRefresh={fetchQueues} />}
		</Box>
	);
}

function SeqQueryPanel({ queueMap = {}, queueLoading = false, lastMsgMap = {}, lastMsgLoading = false, versionMap = {}, versionLoading = false }) {
	const defaults = getDefaultRange();
	const [query, setQuery] = React.useState('SELECT * FROM stream LIMIT 50');
	const [rangeStart, setRangeStart] = React.useState(defaults.start);
	const [rangeEnd, setRangeEnd] = React.useState(defaults.end);
	const [loading, setLoading] = React.useState(false);
	const [rows, setRows] = React.useState(null);
	const [columns, setColumns] = React.useState([]);
	const [error, setError] = React.useState(null);
	const [copied, setCopied] = React.useState(false);
	const [copiedFiltered, setCopiedFiltered] = React.useState(false);
	const [displayedData, setDisplayedData] = React.useState(null);

	function downloadJson(data, filename) {
		const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();
		URL.revokeObjectURL(url);
	}

	function copyAll() {
		navigator.clipboard.writeText(JSON.stringify(rows, null, 2)).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	}

	function copyFiltered() {
		const data = displayedData !== null ? displayedData : rows;
		navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
			setCopiedFiltered(true);
			setTimeout(() => setCopiedFiltered(false), 2000);
		});
	}

	function runQuery() {
		setLoading(true);
		setError(null);
		setRows(null);
		// input değerleri TR local (UTC+3) — "+03:00" ekleyerek parse et, browser timezone'undan bağımsız
		const toUtc = (trLocal) => new Date(trLocal + ':00+03:00').toISOString();
		const cols = parseColumns(query);
		post('/maytapi/admin/seq/query', {
			query,
			rangeStartUtc: toUtc(rangeStart),
			rangeEndUtc: toUtc(rangeEnd),
		})
			.then((res) => {
				setLoading(false);
				const data = res.data || res;
				if (data.success) {
					const r = data.rows || [];
					setRows(r);
					setColumns(cols.length > 0 ? cols : r.length > 0 && Array.isArray(r[0]) ? r[0].map((_, i) => `col${i}`) : []);
				} else {
					setError(data.message || 'Query failed');
				}
			})
			.catch((err) => {
				setLoading(false);
				setError(err.message || 'Request failed');
			});
	}

	function handleKeyDown(e) {
		if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') runQuery();
	}

	return (
		<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
				{PRESET_QUERIES.map((p) => (
					<Button
						key={p.label}
						variant={query === p.query ? 'contained' : 'outlined'}
						size="small"
						onClick={() => setQuery(p.query)}
						sx={{ borderRadius: 3, textTransform: 'none', fontSize: 12 }}
					>
						{p.label}
					</Button>
				))}
			</Box>

			<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
				<TextField
					label="Range Start"
					type="datetime-local"
					value={rangeStart}
					onChange={(e) => setRangeStart(e.target.value)}
					size="small"
					InputLabelProps={{ shrink: true }}
				/>
				<TextField
					label="Range End"
					type="datetime-local"
					value={rangeEnd}
					onChange={(e) => setRangeEnd(e.target.value)}
					size="small"
					InputLabelProps={{ shrink: true }}
				/>
			</Box>

			<TextField
				label="Query (Ctrl+Enter to run)"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				onKeyDown={handleKeyDown}
				multiline
				minRows={3}
				fullWidth
				variant="outlined"
				InputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
			/>

			<Box>
				<Button
					variant="contained"
					startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
					onClick={runQuery}
					disabled={loading}
				>
					Run Query
				</Button>
			</Box>

			{error && (
				<Typography color="error" variant="body2">
					{error}
				</Typography>
			)}

			{rows !== null && (
				<Box>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
						<Typography variant="caption" color="text.secondary">
							{query.includes('boot_instance_size') && displayedData !== null && displayedData.length !== rows.length
								? `${displayedData.length} / ${rows.length} row(s)`
								: `${rows.length} row(s)`}
						</Typography>
						<Box sx={{ display: 'flex', gap: 1 }}>
							{query.includes('boot_instance_size') && (
								<>
									<Tooltip title="Filtreli sonucu kopyala">
										<Button
											size="small"
											variant={copiedFiltered ? 'contained' : 'outlined'}
											color={copiedFiltered ? 'success' : 'primary'}
											startIcon={copiedFiltered ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
											onClick={copyFiltered}
											sx={{ textTransform: 'none', fontSize: 12 }}
										>
											{copiedFiltered ? 'Copied' : 'Filtered'}
										</Button>
									</Tooltip>
									<Tooltip title="Filtreli sonucu indir">
										<Button
											size="small"
											variant="outlined"
											color="primary"
											startIcon={<DownloadIcon fontSize="small" />}
											onClick={() => downloadJson(displayedData !== null ? displayedData : rows, 'seq-filtered.json')}
											sx={{ textTransform: 'none', fontSize: 12 }}
										>
											Filtered
										</Button>
									</Tooltip>
								</>
							)}
							<Tooltip title="Tüm sonucu kopyala">
								<Button
									size="small"
									variant={copied ? 'contained' : 'outlined'}
									color={copied ? 'success' : 'inherit'}
									startIcon={copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
									onClick={copyAll}
									sx={{ textTransform: 'none', fontSize: 12 }}
								>
									{copied ? 'Copied' : 'All'}
								</Button>
							</Tooltip>
							<Tooltip title="Tüm sonucu indir">
								<Button
									size="small"
									variant="outlined"
									color="inherit"
									startIcon={<DownloadIcon fontSize="small" />}
									onClick={() => downloadJson(rows, 'seq-all.json')}
									sx={{ textTransform: 'none', fontSize: 12 }}
								>
									All
								</Button>
							</Tooltip>
						</Box>
					</Box>
					{query.includes('boot_instance_size') ? (
						<InstanceSizeTable rows={rows} onDisplayedChange={setDisplayedData} queueMap={queueMap} queueLoading={queueLoading} lastMsgMap={lastMsgMap} lastMsgLoading={lastMsgLoading} versionMap={versionMap} versionLoading={versionLoading} />
					) : (
						<Box
							sx={{
								backgroundColor: '#282c34',
								borderRadius: 1,
								border: '1px solid #2c313a',
								maxHeight: 600,
								overflow: 'auto',
							}}
						>
							{rows.length === 0 ? (
								<Typography sx={{ p: 2, color: '#6b737e', fontFamily: 'monospace', fontSize: 12 }}>
									No results
								</Typography>
							) : (
								rows.map((row, i) => <LogRow key={i} row={row} columns={columns} index={i} />)
							)}
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
}
